// welcome_screen.view.tsx
import React from "react";
import InfoItem from "../../components/Center Admin/Reuse/InfoItem";
import StatCard from "../../components/Center Admin/Reuse/StatCard";
import TeamMemberCard from "../../components/Center Admin/Reuse/TeamMemberCard";
import EditScreeningTeamModal from "../../components/Center Admin/Reuse/EditScreeningTeamModal";
import "../../center admin css/welcome.css";
import "../../center admin css/centerAdmin.css";
// import '@fontsource/montserrat';
import { ReactComponent as BuildingIcon } from "../../assets/icons/building.svg";
import { ReactComponent as CalendarIcon } from "../../assets/icons/calendar.svg";
import { ReactComponent as ClockIcon } from "../../assets/icons/clock.svg";
import { ReactComponent as ChartIcon } from "../../assets/icons/bar-chart.svg";
import { ReactComponent as RightIcon } from "../../assets/icons/right-tick.svg";
import { ReactComponent as SandIcon } from "../../assets/icons/sand-clock.svg";
import {
  ICenterConfig,
  IScheduleDetails,
  IScreeningCounts,
  IStation,
  IUser,
} from "./WelcomeScreenPage";

// Helper functions (no changes needed)
const getDayName = (): string =>
  new Intl.DateTimeFormat("en-US", { weekday: "long" }).format(new Date());
const getTodayDateString = (): string =>
  new Intl.DateTimeFormat("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  }).format(new Date());
const formatTime = (timeString: string | undefined): string => {
  if (!timeString) return "...";
  const [hours, minutes] = timeString.split(":");
  const date = new Date();
  date.setHours(Number(hours), Number(minutes));
  return new Intl.DateTimeFormat("en-US", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  }).format(date);
};

interface WelcomeScreenViewProps {
  userName: string;
  centerConfig: ICenterConfig | null;
  scheduleDetails: IScheduleDetails | null;
  screeningCounts: IScreeningCounts | null;
  isLoading: boolean;
  error: string | null;
  showEditTeamModal: boolean;

  setShowEditTeamModal: (value: boolean) => void;

  editedStations: IStation[];

  setEditedStations: React.Dispatch<React.SetStateAction<IStation[]>>;

  refreshCenterConfig: () => Promise<void>;
}

const WelcomeScreenView: React.FC<WelcomeScreenViewProps> = ({
  userName,
  centerConfig,
  scheduleDetails,
  screeningCounts,
  isLoading,
  error,
  showEditTeamModal,
  setShowEditTeamModal,
  editedStations,
  setEditedStations,
  refreshCenterConfig,
}) => {
  const todayDayName = getDayName();
  const todaysSlotDesign =
    scheduleDetails?.slotDesigns[todayDayName] ?? "Not Scheduled Today";
  const todayDate = getTodayDateString();

  return (
    <div className="welcome-page">
      <div className="container">
        <div className="card">
          <div className="welcome-section">
            <div className="intro__title">Welcome {userName}!</div>
            <div className="intro__subtitle">
              Here are the details for the screening today:
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card__title">Screening Center Details</div>
          {error && <div className="error-message">{error}</div>}
          {!error && (
            <div className="details-grid">
              <InfoItem
                label="Center Name"
                value={isLoading ? "..." : centerConfig?.centerName}
                icon={<BuildingIcon width={24} height={24} fill="#00A8A8" />}
              />
              <InfoItem
                label="Screening Date"
                value={isLoading ? "..." : todayDate}
                icon={<CalendarIcon width={24} height={24} fill="#00A8A8" />}
              />
              <InfoItem
                label="Slot Design"
                value={isLoading ? "..." : todaysSlotDesign}
                icon={<CalendarIcon width={24} height={24} fill="#00A8A8" />}
              />
              {/* <InfoItem
                label="Screening Start Date"
                value={isLoading ? "..." : scheduleDetails?.screeningStartDate}
                icon={<ClockIcon width={24} height={24} fill="#00A8A8" />}
              />
              <InfoItem
                label="Screening End Date"
                value={isLoading ? "..." : scheduleDetails?.screeningEndDate}
                icon={<ClockIcon width={24} height={24} fill="#00A8A8" />}
              /> */}
            </div>
          )}
        </div>

        <div className="stats-grid">
          <StatCard
            value={
              isLoading ? "..." : (screeningCounts?.totalBookingsToday ?? 0)
            }
            label="Total Screenings Today"
            icon={<ChartIcon width={24} height={24} fill="#00A8A8" />}
          />
          <StatCard
            value={
              isLoading ? "..." : (screeningCounts?.totalCompletedToday ?? 0)
            }
            label="Total Screenings Completed"
            icon={<RightIcon width={24} height={24} fill="#00A8A8" />}
          />
          <StatCard
            value={
              isLoading ? "..." : (screeningCounts?.totalPendingToday ?? 0)
            }
            label="Remaining Screenings"
            icon={<SandIcon width={24} height={24} fill="#00A8A8" />}
          />
          <StatCard
            value={
              isLoading ? "..." : (scheduleDetails?.totalScreeningDays ?? 0)
            }
            label="Total Screening Slots"
            icon={<CalendarIcon width={24} height={24} fill="#00A8A8" />}
          />
        </div>

        <div className="card">
          {/* <div className="card__title">Screening Team</div> */}
          <div className="screening-team-header">
            <div className="card__title">Screening Team</div>

            <button
              className="screening-team-edit-btn"
              onClick={() => {
                if (centerConfig) {
                  setEditedStations(
                    JSON.parse(JSON.stringify(centerConfig.stations)),
                  );
                }

                setShowEditTeamModal(true);
              }}
            >
              Edit
            </button>
          </div>
          {showEditTeamModal && centerConfig && (
            <EditScreeningTeamModal
              centerConfig={centerConfig}
              editedStations={editedStations}
              setEditedStations={setEditedStations}
              refreshCenterConfig={refreshCenterConfig}
              onClose={() => setShowEditTeamModal(false)}
            />
          )}
          <div className="team-grid">
            {isLoading ? (
              <div>Loading team...</div>
            ) : centerConfig && centerConfig.stations.length > 0 ? (
              centerConfig.stations.flatMap((station: IStation) =>
                station.users.map((user: IUser, userIndex: number) => (
                  <TeamMemberCard
                    key={`${station.stationId}-${userIndex}`}
                    name={user.userName}
                    role={station.stationName}
                  />
                )),
              )
            ) : (
              <div>No screening team members available.</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default WelcomeScreenView;
